import { ESdChangeReason, IProjectRole, StartingScreenIcon } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import * as React from "react";
import { DomainService } from "../../../../../services/domain-service";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { aggregateWorkspaceRoles, IWorkspaceRole } from "../../../../management/settings/roles/role-aggregation";
import { KModal } from "../../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../../ui-kit/k-modal/k-modal-footer";
import KuikaBadge from "../../../../ui-kit/kuika-badge/kuika-badge";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInfoBox from "../../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaLoader from "../../../../ui-kit/kuika-loader/kuika-loader";
import KuikaModalIconWrapper from "../../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaH5 from "../../../../ui-kit/typography/H5/H5";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import "./role-modal.scss";

declare let window: any;

export interface IRoleModalState {
  /** Where Create / Update stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Workspace roles that live in OTHER projects — suggested on create so the same name reuses one id. */
  workspaceRoles: IWorkspaceRole[];
  /** The name currently typed, so the create dialog can react (suggest / "extends existing"). */
  typedName: string;
}

export interface IRoleModalProps {
  closeModal: any;
  showModal: boolean;
  id: string;
  name: string;
  startingScreenId: string;
  /** Saves the role. The dialog holds its spinner until the returned promise settles. */
  onChange: (data: IProjectRole) => Promise<void> | void;
  /**
   * Editing only: the panel is still asking which other projects of the domain share this role's
   * name. Purely informational — KOA-15105 made the name read-only on edit, so nothing here can
   * rename the role and UPDATE never waits for the answer.
   */
  isCheckingUsage?: boolean;
  /** Editing only: the other projects that use a role of this name — it is one role across them. */
  usedInProjects?: string[];
  /** Editing only: the usage check itself failed, so the dialog cannot say who else is affected. */
  usageCheckFailed?: boolean;
}

class RoleModal extends PureSdComponent<IRoleModalProps & IWithSDProps, IRoleModalState> {
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IRoleModalProps & IWithSDProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      workspaceRoles: [],
      typedName: ""
    };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  private formRef = React.createRef<FormInstance>();

  private inputRef = React.createRef<HTMLInputElement>();

  componentDidMount() {
    if (this.props.showModal) {
      this.inputRef.current?.focus();
      if (!this.props.id) {
        this.loadWorkspaceRoles();
      }
    }
  }

  componentDidUpdate(prevProps: IRoleModalProps & IWithSDProps) {
    if (!prevProps.showModal && this.props.showModal) {
      this.setState({ typedName: "" });
      if (!this.props.id) {
        this.loadWorkspaceRoles();
      }
      setTimeout(() => {
        if (this.inputRef.current) {
          this.inputRef.current.focus();
        }
      }, 100);
    }
  }

  // Create dialog only: the domain's roles, folded by name. A name that already exists in another
  // project is the SAME role, so typing it should extend that role (reuse its id) rather than mint
  // a new one — the aggregation's roleIds[0] is that canonical id.
  loadWorkspaceRoles = async (): Promise<void> => {
    try {
      const response = await DomainService.GetDomainWideProjectRoles(window.userInfo?.kDomain_Id);
      this.setState({ workspaceRoles: aggregateWorkspaceRoles(response.data) });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  onValuesChange = (changedValues: { name?: string }): void => {
    if (Object.prototype.hasOwnProperty.call(changedValues, "name")) {
      this.setState({ typedName: changedValues.name ?? "" });
    }
  };

  // Workspace roles that aren't already in this project — the ones typing can extend here.
  getExtendableRoles = (): IWorkspaceRole[] => {
    const projectNames: Set<string> = new Set(
      (this.props.sd.project.roles ?? []).map((role) => (role.name ?? "").trim().toLowerCase())
    );
    return this.state.workspaceRoles.filter((role) => !projectNames.has(role.name.trim().toLowerCase()));
  };

  getMatchedRole = (name: string): IWorkspaceRole | undefined => {
    const key: string = (name ?? "").trim().toLowerCase();
    if (key.length === 0) {
      return undefined;
    }
    return this.getExtendableRoles().find((role) => role.name.trim().toLowerCase() === key);
  };

  applySuggestion = (name: string): void => {
    this.formRef.current?.setFieldsValue({ name });
    this.setState({ typedName: name });
  };

  // The dialog waits for the save to land (the project PATCH), then holds its ✓ before it leaves —
  // it used to show ✓ at once and close while the save was still on its way.
  onSubmit = async (data: any) => {
    // The commit is live again as soon as the ✓ replaces the spinner, so a second press would
    // re-save behind the closing dialog.
    if (!this.saveStatus.canStart) return;
    try {
      // One role name per project, ignoring case and surrounding space — otherwise the same pair is
      // one role on SQL Server and two on PostgreSQL. Editing carries the name over untouched,
      // because trimming it there would be a rename nobody asked for.
      const isEditMode: boolean = !!this.props?.id;
      const roleName: string = isEditMode ? this.props.name : (data.name ?? "").trim();
      const nameKey: string = roleName.trim().toLowerCase();

      // Only a new name can collide, and checking on edit would block a project that already
      // carries a "testrole" / "TESTROLE" pair from being edited at all.
      if (
        !isEditMode &&
        this.props.sd.project.roles?.find((role) => (role.name ?? "").trim().toLowerCase() === nameKey)
      ) {
        KMainFunctions.displayWarningNotification("Role Name is already exist");
        return;
      }
      this.saveStatus.start();

      // Typing an existing workspace role name EXTENDS that role here: reuse its canonical id so the
      // same role doesn't get a second id in this project. A genuinely new name keeps id falsy, so
      // the provider mints a fresh GUID (a brand-new role).
      const matchedRole: IWorkspaceRole | undefined = isEditMode ? undefined : this.getMatchedRole(roleName);

      const obj: IProjectRole = {
        name: roleName,
        startingScreenId: data.startingScreenId,
        id: matchedRole ? matchedRole.roleIds[0] : data.id
      };

      await this.props.onChange(obj);
      this.saveStatus.succeed((): void => this.props.closeModal("success"));
    } catch (error) {
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(error);
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  closeModal = () => {
    // A save in flight finishes against this dialog; closing under it would drop its ✓.
    if (this.state.saveStatus === "loading") return;
    this.props.closeModal();
  };

  renderUsageNotice = (): React.ReactNode => {
    if (this.props.isCheckingUsage) {
      return (
        <KuikaInfoBox
          color="gray"
          className="kuika-role-modal__notice"
          leftUnit={<KuikaLoader size="sm" decorative />}
          rightUnit={
            <KuikaSmallText data-testid="roleUsageChecking">
              Checking whether other projects use this role…
            </KuikaSmallText>
          }
        />
      );
    }
    if (this.props.usedInProjects?.length) {
      return (
        <KuikaInfoBox
          color="yellow"
          className="kuika-role-modal__notice"
          leftUnit={<KuikaSvg name="circle-warning" width={16} height={16} />}
          rightUnit={
            <KuikaSmallText data-testid="roleUsageNotice">
              This role is also used in {this.props.usedInProjects.join(", ")} — the same role, one name across the
              domain.
            </KuikaSmallText>
          }
        />
      );
    }
    if (this.props.usageCheckFailed) {
      return (
        <KuikaInfoBox
          color="gray"
          className="kuika-role-modal__notice"
          leftUnit={<KuikaSvg name="circle-warning" width={16} height={16} />}
          rightUnit={
            <KuikaSmallText data-testid="roleUsageNotice">
              Couldn't check whether other projects use this role.
            </KuikaSmallText>
          }
        />
      );
    }
    return null;
  };

  // Create dialog: a match tells the user they're EXTENDING an existing role here (reusing its id);
  // a partial match offers the existing names to pick from. A novel name shows nothing — it's new.
  renderCreateSuggestions = (): React.ReactNode => {
    const matched: IWorkspaceRole | undefined = this.getMatchedRole(this.state.typedName);
    if (matched) {
      const projects: string = matched.occurrences
        .map((occurrence) => occurrence.projectName)
        .filter((name, index, all) => all.indexOf(name) === index)
        .join(", ");
      return (
        <KuikaInfoBox
          color="blue"
          className="kuika-role-modal__notice"
          leftUnit={<KuikaSvg name="info-circle" width={16} height={16} />}
          rightUnit={
            <KuikaSmallText data-testid="roleExtendNotice">
              "{matched.name}" already exists in {projects} — saving adds that role to this project, not a new one.
            </KuikaSmallText>
          }
        />
      );
    }
    const needle: string = this.state.typedName.trim().toLowerCase();
    if (needle.length === 0) {
      return null;
    }
    const suggestions: IWorkspaceRole[] = this.getExtendableRoles()
      .filter((role) => role.name.toLowerCase().includes(needle))
      .slice(0, 6);
    if (suggestions.length === 0) {
      return null;
    }
    return (
      <div className="kuika-role-modal__suggestions">
        <KuikaSmallText>Existing roles — pick to add it here:</KuikaSmallText>
        <div className="kuika-role-modal__suggestion-chips">
          {suggestions.map((role) => (
            <KuikaBadge
              key={role.name}
              type="label"
              color="blue"
              variant="subtle"
              size="small"
              content={role.name}
              onClick={() => this.applySuggestion(role.name)}
            />
          ))}
        </div>
      </div>
    );
  };

  render() {
    const isEditMode = !!this.props?.id;
    const header = isEditMode ? "Edit Role" : "New Role";
    const isSaving: boolean = this.state.saveStatus === "loading";
    const footer = (
      <KModalFooter
        onCancel={this.closeModal}
        submitLabel={isEditMode ? "Update" : "Create"}
        submitTestId="roleModalUpdateCreateButton"
        onSubmit={() => {
          this.formRef.current?.submit();
        }}
        status={this.state.saveStatus}
      />
    );

    const screenOptions =
      this.props.sd?.screenList
        .sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        })
        .map((screen) => ({
          value: screen.id,
          label: (
            // data-testid so E2E can pick a screen from this dropdown by name. rc-select renders
            // `label` twice — once per dropdown option and once as the selected value — so a
            // getByTestId(...) here resolves to two elements ONCE a screen is selected; scope the
            // click to before the selection and read the selection back off the select container.
            <div className="kuika-role-modal__screen-option" data-testid="roleStartingScreenOption">
              <KuikaText>{screen.name}</KuikaText>
              {this.props.sd.project.startingScreenId === screen.id && <StartingScreenIcon width={24} height={24} />}
            </div>
          ),
          searchLabel: screen.name
        })) || [];

    return (
      <KModal
        showModal={this.props.showModal}
        closeModal={this.closeModal}
        header={header}
        footer={footer}
        width={460}
        centered
        isLoading={isSaving}
        closeDisabled={isSaving}
      >
        <div className="kuika-role-modal">
          <div className="kuika-role-modal__header">
            <KuikaModalIconWrapper iconName="shield" intent={isEditMode ? "edit" : "create"} />
            <div className="kuika-role-modal__title">
              <KuikaH5>{header}</KuikaH5>
            </div>
          </div>
          <div className="kuika-role-modal__content">
            {isEditMode && this.renderUsageNotice()}
            <Form
              id="role-form"
              ref={this.formRef}
              name="basic"
              onFinish={this.onSubmit}
              onFinishFailed={this.onFinishFailed}
              onValuesChange={this.onValuesChange}
              initialValues={
                isEditMode
                  ? {
                      name: this.props?.name,
                      id: this.props?.id,
                      startingScreenId: this.props.startingScreenId
                    }
                  : undefined
              }
            >
              <div className="kuika-role-modal__form-item">
                <div className="kuika-role-modal__form-item-header">
                  <KuikaText className="kuika-role-modal__form-item-label">Role Name</KuikaText>
                  <KuikaTooltip
                    content={
                      isEditMode
                        ? "A role name cannot be changed. To rename a role, delete it and add it again with the new name."
                        : "Create a new role to assign specific screens and elements within the application."
                    }
                    position="top"
                  >
                    <div className="kuika-role-modal__form-item-header-icon">
                      <KuikaSvg name="info-circle" width={16} height={16} pointer />
                    </div>
                  </KuikaTooltip>
                </div>
                <Form.Item
                  name="name"
                  rules={[{ required: true, whitespace: true, message: "Name should not empty!" }]}
                >
                  <KuikaInput
                    data-testid="roleNameInput"
                    ref={this.inputRef}
                    placeholder="Enter role name"
                    size="lg"
                    disabled={isEditMode}
                  />
                </Form.Item>
                {!isEditMode && this.renderCreateSuggestions()}
              </div>

              <div className="kuika-role-modal__form-item">
                <div className="kuika-role-modal__form-item-header">
                  <KuikaText className="kuika-role-modal__form-item-label">Starting Screen</KuikaText>
                  <KuikaTooltip
                    content="Select the initial screen that users assigned to this role will see upon logging into the app."
                    position="top"
                  >
                    <div className="kuika-role-modal__form-item-header-icon">
                      <KuikaSvg name="info-circle" width={16} height={16} pointer />
                    </div>
                  </KuikaTooltip>
                </div>
                <Form.Item name="startingScreenId">
                  <KuikaSelectbox
                    options={screenOptions}
                    placeholder="Role starting screen (Optional)"
                    allowClear
                    searchable
                    size="lg"
                    overlayClassName="kuika-role-modal__selectbox-overlay"
                    data-testid="roleStartingScreenDropdown"
                  />
                </Form.Item>
              </div>

              <Form.Item name="id" noStyle>
                <input type="hidden" />
              </Form.Item>
            </Form>
          </div>
        </div>
      </KModal>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.UpdateAuth ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi
    ) {
      return true;
    }
    return false;
  };
}

const roleModal = withSD<IRoleModalProps>(RoleModal);
export { roleModal as RoleModal };
